import type { ReactNode } from 'react';
import { useRef, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, Image } from 'react-native';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import type { ApiMatch } from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import {
  matchweekStatus,
  type Matchweek,
} from '../../../src/competitions/helpers/matchweekSections.helper';
import {
  SeasonMatchPickCard,
  type SeasonPickValue,
  type SeasonPickBoost,
} from './SeasonMatchPickCard';
import {
  SEASON_PICKS_EMPTY_TITLE,
  SEASON_PICKS_EMPTY_SUBTITLE,
  SEASON_PICKS_ERROR_TITLE,
  SEASON_PICKS_ERROR_RETRY,
  SEASON_PICKS_PREV_MATCHWEEK,
  SEASON_PICKS_NEXT_MATCHWEEK,
  SEASON_PICKS_TEST_IDS,
} from './SeasonPicks.constants';

/** Approximate chip pitch (width + gap) — enough to bring the selection into view. */
const CHIP_SCROLL_PITCH = 74;
import { getSeasonPicksStyles } from './SeasonPicks.styles';

export interface SeasonPicksViewProps {
  title: string;
  matchweeks: Matchweek<ApiMatch>[];
  selectedRound: string | null;
  onSelectRound: (round: string) => void;
  resolvePick: (match: ApiMatch) => SeasonPickValue | null;
  /**
   * The tie aggregate for a knockout second leg (specs/champions-league AC-6.9). Optional: a
   * season pool has no ties and passes nothing, so its cards are unchanged.
   */
  resolveAggregate?: (match: ApiMatch) => { home: number; away: number } | null;
  saveErrorMatchIds: ReadonlySet<string>;
  onChangePick: (matchId: string, home: number, away: number) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onBack: () => void;
  /** False when the competition shell already renders a header above this screen. */
  showHeader?: boolean;
  /**
   * Section-flavour copy. Defaults are the season-pool wording, so every existing caller
   * is byte-identical; TOURNAMENT competitions pass matchday/stage wording instead
   * (specs/champions-league AC-2.2). This view is now shared by both formats.
   */
  copy?: {
    emptyTitle?: string;
    emptySubtitle?: string;
    prevLabel?: string;
    nextLabel?: string;
  };
  /** Chip text for a section. Defaults to the season rail's `MW <n>`. */
  renderChipLabel?: (section: Matchweek<ApiMatch>) => string;
  /** Golden Ball state for a match. Omitted for season pools, which have no boost. */
  resolveBoost?: (match: ApiMatch) => SeasonPickBoost | null;
  /**
   * Card action row (specs/champions-league AC-10.3). Passed by the tournament screen only;
   * a season pool passes neither and its cards render without the row (AC-10.8).
   */
  onStatsPress?: (matchId: string) => void;
  onFixturePress?: (matchId: string) => void;
  /**
   * Rendered above the fixture list, once per screen rather than per round: the winner and
   * top-scorer picks for a competition that offers them (specs/champions-league Story 15).
   * Omitted everywhere else, so every existing caller is byte-identical.
   */
  futuresSlot?: ReactNode;
}

export function SeasonPicksView({
  title, matchweeks, selectedRound, onSelectRound, resolvePick, resolveAggregate, saveErrorMatchIds,
  onChangePick, isLoading, isError, onRetry, onBack, showHeader = true,
  copy, renderChipLabel, resolveBoost, onStatsPress, onFixturePress, futuresSlot,
}: SeasonPicksViewProps) {
  const theme = useTheme();
  const s = useMemo(() => getSeasonPicksStyles(theme), [theme]);

  const listRef = useRef<ScrollView>(null);
  useEffect(() => {
    listRef.current?.scrollTo({ y: 0, animated: false });
  }, [selectedRound]);

  // Keep the selected chip in view — on a 14-matchweek rail it otherwise sits off
  // screen on mount, which is what made played matchweeks look absent (AC-13.3).
  // Declared here, above the loading/error/empty early returns, so hook order is
  // stable across every render path.
  const railRef = useRef<ScrollView>(null);
  useEffect(() => {
    const index = matchweeks.findIndex((mw) => mw.round === selectedRound);
    if (index < 0) return;
    railRef.current?.scrollTo({
      x: Math.max(0, (index - 1) * CHIP_SCROLL_PITCH),
      animated: true,
    });
  }, [matchweeks, selectedRound]);

  const header = !showHeader ? null : (
    <View style={s.headerRow}>
      <Pressable
        style={s.backButton}
        onPress={onBack}
        testID={SEASON_PICKS_TEST_IDS.back}
        accessibilityRole="button"
        accessibilityLabel="Back"
      >
        <ChevronLeft size={22} color={theme.gray[400]} />
      </Pressable>
      <Text style={s.headerTitle} numberOfLines={1}>{title}</Text>
    </View>
  );

  const wrap = (children: React.ReactNode) => (
    <View style={s.screen} testID={SEASON_PICKS_TEST_IDS.screen}>
      <View style={s.column}>{children}</View>
    </View>
  );

  if (isLoading) {
    return wrap(
      <>
        {header}
        <View style={s.listContent}>
          <View style={s.skeletonCard} />
          <View style={s.skeletonCard} />
          <View style={s.skeletonCard} />
        </View>
      </>,
    );
  }

  if (isError) {
    return wrap(
      <>
        {header}
        <View style={s.centerWrap}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{SEASON_PICKS_ERROR_TITLE}</Text>
          <Pressable style={s.retryButton} onPress={onRetry} testID={SEASON_PICKS_TEST_IDS.retry} accessibilityRole="button">
            <Text style={s.retryLabel}>{SEASON_PICKS_ERROR_RETRY}</Text>
          </Pressable>
        </View>
      </>,
    );
  }

  if (matchweeks.length === 0) {
    return wrap(
      <>
        {header}
        <View style={s.centerWrap}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{copy?.emptyTitle ?? SEASON_PICKS_EMPTY_TITLE}</Text>
          <Text style={s.stateSubtitle}>{copy?.emptySubtitle ?? SEASON_PICKS_EMPTY_SUBTITLE}</Text>
        </View>
      </>,
    );
  }

  const selected = matchweeks.find((mw) => mw.round === selectedRound) ?? matchweeks[0];
  const selectedIndex = matchweeks.findIndex((mw) => mw.round === selected.round);
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex >= 0 && selectedIndex < matchweeks.length - 1;

  // Plain function, not useCallback — this sits below the early returns, so a hook
  // here would break hook order on the loading/error/empty paths.
  const step = (delta: -1 | 1) => {
    const next = matchweeks[selectedIndex + delta];
    if (next) onSelectRound(next.round);
  };

  return wrap(
    <>
      {header}
      {/* Arrows are not a nicety: the rail is a hidden-scrollbar horizontal
          ScrollView, which a mouse cannot drag, so on web they are the only way
          to reach matchweeks past the fold (AC-13.4). */}
      <View style={s.railRow}>
        <Pressable
          style={[s.railArrow, !hasPrev && s.railArrowDisabled]}
          onPress={() => step(-1)}
          disabled={!hasPrev}
          testID={SEASON_PICKS_TEST_IDS.prevMatchweek}
          accessibilityRole="button"
          accessibilityState={{ disabled: !hasPrev }}
          accessibilityLabel={copy?.prevLabel ?? SEASON_PICKS_PREV_MATCHWEEK}
        >
          <ChevronLeft size={20} color={theme.gray[400]} />
        </Pressable>
        <ScrollView
          ref={railRef}
          style={s.railScroll}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={s.chipsRow}
        >
          {matchweeks.map((mw) => {
            const isSelected = mw.round === selected.round;
            const status = matchweekStatus(mw);
            return (
              <Pressable
                key={mw.round}
                style={[s.chip, status === 'finished' && s.chipFinished, isSelected && s.chipSelected]}
                onPress={() => onSelectRound(mw.round)}
                testID={SEASON_PICKS_TEST_IDS.chip(mw.round)}
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={`${mw.label}, ${status}`}
              >
                {status === 'live' && <View style={s.chipLiveDot} />}
                <Text style={[s.chipLabel, isSelected && s.chipLabelSelected]}>
                  {renderChipLabel ? renderChipLabel(mw) : `MW ${mw.round}`}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
        <Pressable
          style={[s.railArrow, !hasNext && s.railArrowDisabled]}
          onPress={() => step(1)}
          disabled={!hasNext}
          testID={SEASON_PICKS_TEST_IDS.nextMatchweek}
          accessibilityRole="button"
          accessibilityState={{ disabled: !hasNext }}
          accessibilityLabel={copy?.nextLabel ?? SEASON_PICKS_NEXT_MATCHWEEK}
        >
          <ChevronRight size={20} color={theme.gray[400]} />
        </Pressable>
      </View>
      <ScrollView
        ref={listRef}
        contentContainerStyle={s.listContent}
        showsVerticalScrollIndicator={false}
      >
        {futuresSlot}
        {selected.matches.map((match) => (
          <SeasonMatchPickCard
            key={match.matchId}
            match={match}
            pick={resolvePick(match)}
            hasSaveError={saveErrorMatchIds.has(match.matchId)}
            onChangePick={onChangePick}
            boost={resolveBoost ? resolveBoost(match) : null}
            aggregate={resolveAggregate ? resolveAggregate(match) : null}
            onStatsPress={onStatsPress}
            onFixturePress={onFixturePress}
          />
        ))}
      </ScrollView>
    </>,
  );
}
